import { useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexRow } from 'ui-components/components/flex';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { NOTIFICATION_TYPE_ERROR, NotificationService } from 'ui-components/services/notification-service';
import { Text, TextType } from 'ui-components/components/text';

import { SimpleModal } from '../../../../../components/simple-modal';
import { useModal } from '../../../../../hooks/use-modal';
import { useProductDetailsContext } from '../../../use-product-details-context';
import { deleteSupplierProduct } from '../../../../../pages/products-page/suppliers/api/supplier-products-api';
import { useProductSuppliers } from './use-product-suppliers';
import { AddSupplierModal } from './add-supplier-modal/add-supplier-modal';
import { EditSupplierModal } from './edit-supplier-modal/edit-supplier-modal';
import { SupplierRow } from './components/supplier-row/supplier-row';
import type { ProductSupplier } from './product-suppliers-types';
import { messages } from './product-suppliers-tab.messages';
import style from './product-suppliers-tab.module.less';

export const ProductDetailsSuppliersV3 = () => {
  const intl = useIntl();
  const { productId } = useProductDetailsContext();
  const { suppliers, isLoading, hasError, reload } = useProductSuppliers(productId);
  const { isOpen: isAddModalOpen, open: openAddModal, close: closeAddModal } = useModal(false);

  const [editingSupplier, setEditingSupplier] = useState<ProductSupplier | null>(null);
  const [deletingSupplier, setDeletingSupplier] = useState<ProductSupplier | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openEditModal = useCallback((supplier: ProductSupplier) => {
    setEditingSupplier(supplier);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingSupplier(null);
  }, []);

  const openDeleteConfirm = useCallback((supplier: ProductSupplier) => {
    setDeletingSupplier(supplier);
  }, []);

  const closeDeleteConfirm = useCallback(() => {
    setDeletingSupplier(null);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deletingSupplier) return;

    setIsDeleting(true);
    try {
      await deleteSupplierProduct(deletingSupplier.productSupplierGuid);
      setDeletingSupplier(null);
      reload();
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        content: intl.formatMessage(messages.deleteError),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deletingSupplier, reload, intl]);

  return (
    <>
      {isAddModalOpen && <AddSupplierModal close={closeAddModal} onSuccess={reload} />}
      {editingSupplier && (
        <EditSupplierModal
          close={closeEditModal}
          initialCost={editingSupplier.cost}
          initialSupplierSku={editingSupplier.supplierSku}
          onSuccess={reload}
          productSupplierGuid={editingSupplier.productSupplierGuid}
        />
      )}
      {deletingSupplier && (
        <SimpleModal
          actionButtonTheme={THEME_CONSTANTS.danger}
          actionMessage={messages.deleteConfirmAction}
          cancelMessage={messages.deleteConfirmCancel}
          headerMessage={messages.deleteConfirmTitle}
          isActionDisabled={isDeleting}
          onAction={handleDelete}
          onCancel={closeDeleteConfirm}
          onClose={closeDeleteConfirm}
        >
          <Text element='div' textType={TextType.Paragraph} value={messages.deleteConfirmBody} />
        </SimpleModal>
      )}
      <div className={style['suppliers-tab']}>
        <div className={style['content-card']}>
          <FlexRow className={style['card-header']}>
            <div>
              <Text element='div' textType={TextType.H4} value={messages.title} />
              <Text className={style['card-subtitle']} element='div' textType={TextType.Caption} value={messages.subtitle} />
            </div>
            <Button className={style['add-supplier-btn']} onClick={openAddModal} theme={THEME_CONSTANTS.secondary}>
              <FontAwesomeIcon icon={faPlus} />
              <FormattedMessage {...messages.assignSupplierButton} />
            </Button>
          </FlexRow>

          <div className={style['suppliers-grid']}>
            <div className={style['suppliers-grid-header']}>
              <Text element='div' textType={TextType.Label} value={messages.columnSupplierName} />
              <Text element='div' textType={TextType.Label} value={messages.columnUomSku} />
              <Text element='div' textType={TextType.Label} value={messages.columnSupplierSku} />
              <Text element='div' textType={TextType.Label} value={messages.columnCost} />
              <div />
            </div>

            {isLoading ? (
              <div className={style['suppliers-loading']}>
                <LoadingIndicator theme='grey' />
              </div>
            ) : (
              <>
                {hasError && (
                  <div className={style['suppliers-feedback']}>
                    <Text element='div' textType={TextType.Paragraph} value={messages.loadingError} />
                  </div>
                )}

                {!hasError && suppliers.length === 0 && (
                  <div className={style['suppliers-feedback']}>
                    <Text element='div' textType={TextType.Paragraph} value={messages.emptyState} />
                  </div>
                )}

                {suppliers.map((supplier) => (
                  <SupplierRow key={supplier.productSupplierGuid} onDelete={openDeleteConfirm} onEdit={openEditModal} supplier={supplier} />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
